// Type definitions
export type ConversationType = 'vendor' | 'resident' | 'coordinator' | 'technician';
export type SenderType = ConversationType | 'system';
export type MessageType = 'text' | 'system' | 'attachment';
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

// WebSocket Event Names
export const CHAT_EVENTS = {
  WORK_ORDER_MESSAGE_RECEIVED: '.work-order.message.received',
  SERVICE_REQUEST_MESSAGE_RECEIVED: '.service-request.message.received',
  CONVERSATION_MESSAGE_CREATED: '.conversation.message.created',
  CONVERSATION_MESSAGE_DELIVERED: '.conversation.message.delivered',
  CONVERSATION_READ_RECEIPT: '.conversation.read.receipt',
  CONVERSATION_TYPING_STARTED: '.conversation.typing.started',
  CONVERSATION_TYPING_STOPPED: '.conversation.typing.stopped',
  CONVERSATION_CREATED: '.conversation.created',
} as const;

// WebSocket Channel Names
//
// The two overview channels are now org-scoped and private (APX-345): the
// backend broadcasts `conversation.created` on
// `organization.{orgId}.{work-orders|service-requests}.conversations` as a
// PrivateChannel, so the client must build the org-scoped name and subscribe
// via `echo.private(...)`. These are builder functions rather than constants
// because the org id is only known at runtime (from the signed-in profile).
export const CHAT_CHANNELS = {
  SERVICE_REQUESTS_CONVERSATIONS: (orgId: string) =>
    `organization.${orgId}.service-requests.conversations`,
  WORK_ORDERS_CONVERSATIONS: (orgId: string) => `organization.${orgId}.work-orders.conversations`,
} as const;

export interface Message {
  id: number;
  conversation_id: string; // UUID
  content: string;
  sender_id: number;
  sender_type: string;
  created_at: string;
  updated_at: string;
  attachments?: MessageAttachment[];
  is_read?: boolean;
}

export interface MessageAttachment {
  id?: string;
  uuid?: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: string;
  formatted_size: string;
  url: string;
  thumbnail?: string;
  thumbnail_url?: string;
  original?: string;
  is_image: boolean;
  is_document: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkippedFile {
  original_name: string;
  reason: string;
}

export interface UploadImagesResponse {
  message: string;
  // Top-level id of the message the backend created for this upload. Equals the
  // Pusher broadcast echo's `data.uuid`, so reconciling the optimistic message's
  // uuid/id to this value lets `addIncomingMessage` dedup the echo instead of
  // appending a duplicate row. May be absent when no message was created
  // (e.g. every file was skipped). (ORI-452)
  uuid?: string;
  files: MessageAttachment[];
  skipped_files: SkippedFile[];
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
  data: ConversationMessage;
  meta: {
    message_id: number;
    conversation_id: number;
    timestamp: string;
  };
}

export interface ConversationParticipant {
  id?: string;
  uuid?: string;
  conversation_id?: string;
  participant_id: string;
  participant_type: string;
  participant_type_label: string;
  participant_name?: string | null;
  is_coordinator: boolean;
  is_vendor: boolean;
  is_resident: boolean;
  is_internal: boolean;
  is_external: boolean;
  joined_at: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id?: string;
  uuid?: string;
  conversation_id?: string;
  // How `MessageResource` names the owning conversation — the REST payloads carry
  // this and no `conversation_id`. Messages that arrive over the websocket carry the
  // same uuid under `conversation_id` instead (the event mapping fills it in), so
  // either field can be the one that identifies the conversation. (COM-687)
  conversation_uuid?: string;
  sender_id: string;
  sender_type: SenderType;
  sender_type_label: string;
  sender_name?: string;
  message_content: string;
  message_type: MessageType;
  message_type_label: string;
  is_system: boolean;
  has_attachment: boolean;
  status?: MessageStatus;
  status_label?: string;
  checkmark_count?: string | number;
  has_single_checkmark?: boolean;
  has_double_checkmark?: boolean;
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at: string | null;
  is_sent?: boolean;
  is_delivered?: boolean;
  is_read: boolean;
  attachments: MessageAttachment[];
  created_at: string;
  updated_at: string;
}

/**
 * A `ConversationMessage` as it enters the store from a real-time event, carrying the
 * dispatcher's verdict on whether the current user authored it. `isOwnMessage` is REQUIRED,
 * not optional: the compensating unread-decrement that once masked a forgotten flag is gone
 * (ORI-644 Part B), so a dispatcher that omits it must fail to compile rather than silently
 * regress the fix (a self-echo would fall through to the incoming branch and bump the badge).
 * Transport-only — `addIncomingMessage` strips it before storing. (ORI-644) */
export type IncomingConversationMessage = ConversationMessage & { isOwnMessage: boolean };

export interface Conversation {
  id?: string; // UUID - for backwards compatibility
  uuid?: string; // UUID - API uses this field
  organization_id?: string | number;
  service_request_uuid?: string;
  service_request_number?: string;
  service_request_id?: string;
  work_order_uuid?: string;
  work_order_number?: string;
  work_order_id?: string | null;
  conversation_type: string;
  conversation_type_label: string;
  is_vendor_conversation: boolean;
  is_resident_conversation: boolean;
  is_coordinator_conversation: boolean;
  is_technician_conversation: boolean;
  subject?: string | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  participants: ConversationParticipant[];
  messages: ConversationMessage[];
  participants_count: number;
  messages_count: number;
  unread_messages_count: number;
  latest_message?: ConversationMessage;
  // Pagination state for messages
  messages_next_url?: string | null;
  messages_has_more?: boolean;
}

export interface ConversationsResponse {
  success: boolean;
  data: Conversation[];
  meta: {
    service_request_id: string;
    conversations_count: string;
    loaded_relationships: string[];
    timestamp: string;
  };
}

export interface ConversationResponse {
  success: boolean;
  data: Conversation; // API returns single conversation object
  meta: {
    conversation_id: number;
    loaded_relationships: string[];
    timestamp: string;
    messages_pagination?: {
      total: number;
      count: number;
      per_page: number;
      current_page: number;
      total_pages: number;
      has_more_pages: boolean;
    };
  };
  links?: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

export interface CreateConversationRequest {
  organization_id?: number;
  organization_uuid?: string;
  work_order_uuid?: string;
  work_order_number?: string;
  service_request_uuid?: string;
  service_request_number?: string;
  conversation_type: ConversationType;
  sender_id?: string;
  sender_type?: SenderType;
  sender_name?: string;
  message_content?: string;
  message_type?: MessageType;
}

export interface CreateConversationResponse {
  success: boolean;
  message: string;
  data: Conversation;
  // Top-level id of the first message created alongside the conversation. Equals
  // the Pusher broadcast echo's `data.uuid` (distinct from `data.uuid`, which is
  // the conversation's own uuid). May be null when no message was created — e.g.
  // an attachment-only first send where every file was skipped. (ORI-452)
  uuid?: string | null;
  skipped_files?: SkippedFile[];
  meta: {
    conversation_id: number;
    work_order_uuid?: string;
    service_request_uuid?: string;
    timestamp: string;
  };
}

export interface TypingUser {
  user_id: string;
  user_name: string;
  conversation_uuid?: string;
}

export interface ConversationsCache {
  [entityId: string]: {
    conversations: Conversation[];
    lastFetched: number;
  };
}

export interface ChatState {
  messages: Message[];
  conversations: Conversation[];
  isLoading: boolean;
  error: string | null;
  uploadingFiles: boolean;
  currentConversation?: Conversation | null;
  selectedConversationId: string | null;
  isCreatingConversation?: boolean;
  typingUsers?: TypingUser[];
  serviceRequestConversationsCache: ConversationsCache;
  workOrderConversationsCache: ConversationsCache;
  liveUpdatesPaused?: boolean;
}

export const chatInitialState: ChatState = {
  messages: [],
  conversations: [],
  isLoading: false,
  error: null,
  uploadingFiles: false,
  currentConversation: null,
  selectedConversationId: null,
  serviceRequestConversationsCache: {},
  workOrderConversationsCache: {},
  liveUpdatesPaused: false,
};

export interface InitConversation
  extends Pick<
    Conversation,
    'service_request_uuid' | 'service_request_number' | 'work_order_uuid' | 'work_order_number'
  > {
  organization_uuid?: string;
  conversation_type: string;
  message_content?: string;
  sender_id: string;
  sender_type: string;
  sender_name?: string;
  message_type?: string;
  attachments?: File[];
  participants?: Pick<ConversationParticipant, 'participant_id' | 'participant_type'>[];
  socket_id?: string | null;
}

// WebSocket Event Types
export type WebSocketSenderType =
  | 'coordinator'
  | 'vendor'
  | 'resident'
  | 'technician'
  | 'system'
  | 'system_received';

export type WebSocketMessageType = 'text' | 'system' | 'system_receive' | 'attachment';

export type WebSocketMessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

export interface WebSocketAttachment {
  uuid: string;
  file_name: string;
  file_type: string;
  file_size: number;
  url: string;
  thumbnail_url?: string;
  thumbnail?: string;
}

// Event: conversation.message.created
export interface ConversationMessageCreatedEvent {
  data: {
    uuid: string;
    conversation_uuid?: string;
    sender_id: string;
    sender_type: WebSocketSenderType;
    sender_type_label: string;
    sender_name: string;
    message_content: string;
    message_type: WebSocketMessageType;
    message_type_label: string;
    is_system: boolean;
    has_attachment: boolean;
    status: WebSocketMessageStatus;
    status_label: string;
    checkmark_count: 0 | 1 | 2;
    has_single_checkmark: boolean;
    has_double_checkmark: boolean;
    sent_at: string | null;
    delivered_at: string | null;
    read_at: string | null;
    is_sent: boolean;
    is_delivered: boolean;
    is_read: boolean;
    attachments: WebSocketAttachment[];
    created_at: string;
    updated_at: string;
  };
}

/**
 * The audiences the backend keys `unread_counts` by.
 *
 * Single source for both the type and the runtime list, so a new audience cannot
 * be added to one and forgotten in the other. (ORI-616)
 */
export const UNREAD_AUDIENCES = ['coordinator', 'vendor', 'technician'] as const;

export type UnreadAudience = (typeof UNREAD_AUDIENCES)[number];

/**
 * Unread counts keyed by audience, each excluding the messages that audience sent
 * itself. `work-orders.{uuid}.messages` is shared by coordinator, vendor and
 * technician, so a single flat count cannot be correct for all of them — read your
 * own bucket. Optional because the backend may be older than this client. (ORI-616)
 */
export type UnreadCountsByAudience = Partial<Record<UnreadAudience, number>>;

// Event: work-order.message.received
export interface WorkOrderMessageReceivedEvent {
  data: {
    message_id: string;
    conversation_uuid: string;
    work_order_uuid: string;
    organization_id: number;
    /** Viewer-agnostic total. Prefer `unread_counts[audience]`; retained for older payloads. */
    unread_count: number;
    unread_counts?: UnreadCountsByAudience;
    sender_type: WebSocketSenderType;
    sender_id: string | null;
    sender_name: string | null;
    message_content: string | null;
    message_type: WebSocketMessageType;
    timestamp: string;
  };
}

// Event: service-request.message.received
export interface ServiceRequestMessageReceivedEvent {
  data: {
    message_id: string;
    conversation_uuid: string;
    service_request_uuid: string;
    organization_id: number;
    unread_count: number;
    sender_type: WebSocketSenderType;
    sender_id: string | null;
    sender_name: string | null;
    message_content: string | null;
    message_type: WebSocketMessageType;
    timestamp: string;
  };
}

// Event: conversation.message.delivered
export interface ConversationMessageDeliveredEvent {
  message_id: string;
  conversation_uuid: string;
  work_order_uuid: string | null;
  service_request_uuid: string | null;
  status: 'delivered';
  delivered_at: string;
  checkmark_count: 2;
  timestamp: string;
}

// Event: conversation.messages.delivered
// COM-687: broadcast by the bulk sweep behind POST conversations/{uuid}/delivered,
// which is the only delivered endpoint any client calls. Distinct from the
// singular event above, which has no producer.
// `user_id` is flat here, unlike ConversationReadReceiptEvent's nested `data`.
export interface ConversationMessagesDeliveredEvent {
  conversation_uuid: string;
  work_order_uuid: string | null;
  service_request_uuid: string | null;
  messages_marked_as_delivered: number;
  user_id: string | null;
  timestamp: string;
  // Added by the backend half of this fix (COM-686). Optional so that a
  // frontend deployed ahead of the backend degrades to today's no-op.
  uuids?: string[];
  delivered_at?: string | null;
}

// Event: conversation.read.receipt
export interface ConversationReadReceiptEvent {
  data: {
    conversation_uuid: string;
    messages_marked_as_read: number;
    // The reader's integer PK (consumed server-side). Do not compare this against
    // UUID-spaced ids on the client — use user_uuid for that. (ORI-645)
    user_id: string | null;
    // The reader's UUID — the id-space the client compares against (currentUserId,
    // message.sender_id are all UUIDs). (ORI-645)
    user_uuid: string | null;
    read_at: string;
  };
}

// Event: conversation.typing.started / conversation.typing.stopped
export interface ConversationTypingEvent {
  data: {
    participant_uuid: string; // user_uuid from users table
    participant_id: number;
    participant_id_string: string;
    participant_name: string;
    participant_type: WebSocketSenderType;
    conversation_uuid: string;
  };
}

// Event: conversation.created (broadcast on the org-scoped private overview legs
// organization.{orgId}.service-requests.conversations /
// organization.{orgId}.work-orders.conversations — APX-345)
export interface ConversationCreatedEvent {
  data: Conversation;
  // The org UUID string (ProfileResource maps organization_id => organization_uuid);
  // never a numeric PK on the wire. (APX-345)
  organization_id: string;
  service_request_uuid: string | null;
  work_order_uuid: string | null;
}

// The websocket carries a couple of enum values the domain model doesn't have
// ('system_receive' / 'system_received'). These tables translate each wire value
// to its stored equivalent; TypeScript requires every wire value to be listed,
// so a new backend value fails the build until it is mapped here.
const WEBSOCKET_TO_MESSAGE_TYPE: Record<WebSocketMessageType, MessageType> = {
  text: 'text',
  system: 'system',
  system_receive: 'system',
  attachment: 'attachment',
};

const WEBSOCKET_TO_SENDER_TYPE: Record<WebSocketSenderType, SenderType> = {
  coordinator: 'coordinator',
  vendor: 'vendor',
  resident: 'resident',
  technician: 'technician',
  system: 'system',
  system_received: 'system',
};

/** Domain-typed latest_message preview consumed by updateConversationOverview. */
export interface OverviewLatestMessage {
  message_content: string;
  message_type?: MessageType;
  sender_id: string | null;
  sender_type: SenderType;
  sender_name: string | null;
  created_at: string;
}

/** The fields shared by every `*.message.received` websocket event payload. */
interface WebSocketMessagePreview {
  message_content: string | null;
  message_type: WebSocketMessageType;
  sender_id: string | null;
  sender_type: WebSocketSenderType;
  sender_name: string | null;
  timestamp: string;
}

/**
 * The single wire→domain boundary for chat overviews: turns a raw websocket
 * message event into the domain-typed preview stored in Redux, so wire-only
 * enum values never leak past this point.
 */
export const toOverviewLatestMessage = (data: WebSocketMessagePreview): OverviewLatestMessage => ({
  message_content: data.message_content || '',
  message_type: WEBSOCKET_TO_MESSAGE_TYPE[data.message_type],
  sender_id: data.sender_id,
  sender_type: WEBSOCKET_TO_SENDER_TYPE[data.sender_type],
  sender_name: data.sender_name,
  created_at: data.timestamp,
});
